import type { Rect } from '@/core/layout/geometry';
import type { Fill, Scene, SceneNode } from './scene';

/**
 * Сериализация сцены в самостоятельный SVG.
 *
 * Документ не ссылается ни на какие внешние ресурсы: подпись уже превращена
 * в контуры, шрифты не подключаются, картинок нет. Любая строка, попадающая
 * в разметку, экранируется — пользовательский ввод не может стать тегом.
 */

export function escapeXml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

const num = (v: number): string => {
  const r = Math.round(v * 100) / 100;
  return Object.is(r, -0) ? '0' : String(r);
};

function fillRef(fill: Fill): string {
  return fill.kind === 'solid' ? fill.color : `url(#${fill.id})`;
}

/** Прямоугольник со скруглением только верхних углов. */
export function topRoundedPath(rect: Rect, radius: number): string {
  const r = Math.max(0, Math.min(radius, rect.w / 2, rect.h));
  const { x, y, w, h } = rect;
  return [
    `M${num(x)} ${num(y + r)}`,
    `A${num(r)} ${num(r)} 0 0 1 ${num(x + r)} ${num(y)}`,
    `H${num(x + w - r)}`,
    `A${num(r)} ${num(r)} 0 0 1 ${num(x + w)} ${num(y + r)}`,
    `V${num(y + h)}`,
    `H${num(x)}`,
    'Z',
  ].join('');
}

/**
 * Матрица рисуется одним path: каждая горизонтальная цепочка тёмных модулей
 * становится прямоугольником. Это даёт компактный файл и ровные стыки без
 * тонких светлых щелей между соседними модулями.
 */
export function qrPathData(
  matrix: boolean[][],
  moduleSize: number,
  originX: number,
  originY: number,
  quietZoneModules: number,
): string {
  const parts: string[] = [];
  const startX = originX + quietZoneModules * moduleSize;
  const startY = originY + quietZoneModules * moduleSize;
  for (let y = 0; y < matrix.length; y += 1) {
    const row = matrix[y]!;
    let x = 0;
    while (x < row.length) {
      if (!row[x]) {
        x += 1;
        continue;
      }
      let run = 1;
      while (x + run < row.length && row[x + run]) run += 1;
      parts.push(
        `M${num(startX + x * moduleSize)} ${num(startY + y * moduleSize)}h${num(run * moduleSize)}v${num(moduleSize)}h${num(-run * moduleSize)}z`,
      );
      x += run;
    }
  }
  return parts.join('');
}

function nodeToSvg(node: SceneNode): string {
  switch (node.type) {
    case 'rect': {
      const { x, y, w, h } = node.rect;
      const rx = node.radius > 0 ? ` rx="${num(node.radius)}"` : '';
      return `<rect x="${num(x)}" y="${num(y)}" width="${num(w)}" height="${num(h)}"${rx} fill="${fillRef(node.fill)}"/>`;
    }
    case 'topbar':
      return `<path d="${topRoundedPath(node.rect, node.radius)}" fill="${fillRef(node.fill)}"/>`;
    case 'stroke': {
      const { x, y, w, h } = node.rect;
      const rx = node.radius > 0 ? ` rx="${num(node.radius)}"` : '';
      return `<rect x="${num(x)}" y="${num(y)}" width="${num(w)}" height="${num(h)}"${rx} fill="none" stroke="${node.color}" stroke-width="${num(node.width)}"/>`;
    }
    case 'qr': {
      const d = qrPathData(
        node.matrix,
        node.moduleSize,
        node.rect.x,
        node.rect.y,
        node.quietZoneModules,
      );
      return `<path d="${d}" fill="${node.dark}" shape-rendering="crispEdges"/>`;
    }
    case 'path':
      return `<path d="${node.d}" fill="${node.fill}"/>`;
  }
}

function gradientDefs(scene: Scene): string {
  const gradients = new Map<string, { from: string; to: string }>();
  for (const node of scene.nodes) {
    if ((node.type === 'rect' || node.type === 'topbar') && node.fill.kind === 'gradient') {
      gradients.set(node.fill.id, { from: node.fill.from, to: node.fill.to });
    }
  }
  if (gradients.size === 0) return '';
  const body = [...gradients.entries()]
    .map(
      ([id, g]) =>
        `<linearGradient id="${escapeXml(id)}" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="${g.from}"/><stop offset="1" stop-color="${g.to}"/></linearGradient>`,
    )
    .join('');
  return `<defs>${body}</defs>`;
}

export type SvgOptions = {
  /** Добавить <title> для программ чтения с экрана. */
  accessibleTitle?: boolean;
  /** XML-пролог нужен для отдельного файла, но мешает при вставке в DOM. */
  xmlDeclaration?: boolean;
};

export function sceneToSvg(scene: Scene, options: SvgOptions = {}): string {
  const { accessibleTitle = true, xmlDeclaration = false } = options;
  const title = accessibleTitle
    ? `<title>${escapeXml(scene.meta.description)}</title>`
    : '';
  const body = scene.nodes.map(nodeToSvg).join('');
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="${num(scene.width)}" height="${num(scene.height)}" ` +
    `viewBox="0 0 ${num(scene.width)} ${num(scene.height)}" role="img">` +
    title +
    gradientDefs(scene) +
    body +
    '</svg>';
  return xmlDeclaration ? `<?xml version="1.0" encoding="UTF-8"?>\n${svg}` : svg;
}
